import React from 'react';
import SEO from '@/components/seo/SEO';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { FileText, Gavel, CreditCard, Ban, ShieldAlert, Mail, Scale } from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  const lastUpdated = "January 5, 2026";
  const companyName = "RunOffTrack.com";
  const contactEmail = "danielgaskins99@gmail.com";

  const sections = [
    { id: 'agreement', label: '1. Acceptance of Terms' },
    { id: 'service', label: '2. Description of Service' },
    { id: 'accounts', label: '3. User Accounts' },
    { id: 'payments', label: '4. Fees & Subscriptions' },
    { id: 'conduct', label: '5. Prohibited Conduct' },
    { id: 'liability', label: '6. Limitation of Liability' },
    { id: 'termination', label: '7. Termination' },
    { id: 'governing', label: '8. Governing Law' },
  ];

  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO 
        title="Terms of Service - RunOffTrack.com"
        description="The terms and conditions governing your use of the RunOffTrack.com platform and services."
        canonical="/terms-of-service"
      />

      {/* Hero Section */}
      <div className="bg-muted/30 pt-32 pb-20 border-b border-border">
        <div className="container px-4 text-center max-w-4xl mx-auto">
           <Breadcrumbs items={[{ label: 'Legal', href: '/terms-of-service' }]} />
           
           <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-full mb-6">
             <Gavel size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-4 text-foreground">
             Terms of Service
           </h1>
           <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
             Please read these terms carefully before using our platform. By accessing RunOffTrack.com, you agree to be bound by these conditions.
           </p>
           <div className="mt-6 text-xs font-bold uppercase tracking-widest text-muted-foreground/60">
             Last Updated: {lastUpdated}
           </div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[250px_1fr] gap-16 items-start">
          
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block sticky top-24 space-y-4 border-r border-border pr-8">
            <p className="text-xs font-bold uppercase text-muted-foreground tracking-tighter mb-6">Agreement</p>
            <nav className="flex flex-col space-y-3">
              {sections.map(s => (
                <a 
                  key={s.id} 
                  href={`#${s.id}`} 
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                >
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <article className="prose prose-slate dark:prose-invert max-w-none space-y-16">
            
            <section id="agreement" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><Scale size={20}/></div>
                1. Acceptance of Terms
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                By accessing or using RunOffTrack.com (the "Service"), you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the Service.
              </p>
            </section>

            <Separator />

            <section id="service" className="scroll-mt-24">
              <h2 className="text-2xl font-bold text-foreground">2. Description of Service</h2>
              <p className="text-muted-foreground leading-relaxed">
                RunOffTrack.com provides a cloud-based software-as-a-service (SaaS) platform designed to assist construction site operators in managing Stormwater Pollution Prevention Plan (SWPPP) inspections and regulatory compliance.
              </p>
            </section>

            <section id="accounts" className="scroll-mt-24">
              <h2 className="text-2xl font-bold text-foreground">3. User Accounts</h2>
              <p className="text-muted-foreground">
                When you create an account, you must provide accurate and complete information. You are responsible for safeguarding the password used to access the Service and for any activities or actions under your account.
              </p>
            </section>

            <section id="payments" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><CreditCard size={20}/></div>
                4. Fees & Subscriptions
              </h2>
              <p className="text-muted-foreground">
                Some parts of the Service are billed on a subscription basis. You will be billed in advance on a recurring and periodic basis. 
              </p>
              <Card className="bg-muted/20 border-border">
                <CardContent className="p-4 text-sm text-muted-foreground">
                    <strong>Refund Policy:</strong> Except when required by law, paid subscription fees are non-refundable. Prorated credits may be applied for plan changes.
                </CardContent>
              </Card>
            </section>

            <section id="conduct" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><Ban size={20}/></div>
                5. Prohibited Conduct
              </h2>
              <p className="text-muted-foreground">
                You agree not to use the Service for any unlawful purpose or in any way that interrupts, damages, or impairs the Service. Prohibited activities include attempting to reverse engineer the software or bypassing security measures.
              </p>
            </section>

            <section id="liability" className="scroll-mt-24">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-foreground">
                <div className="p-2 bg-muted rounded-lg"><ShieldAlert size={20}/></div>
                6. Limitation of Liability
              </h2>
              <p className="text-muted-foreground">
                In no event shall RunOffTrack.com, nor its directors, employees, or partners, be liable for any indirect, incidental, special, or consequential damages resulting from your use of the Service or any regulatory fines incurred at your construction sites.
              </p>
            </section>

            <section id="termination" className="scroll-mt-24">
              <h2 className="text-2xl font-bold text-foreground">7. Termination</h2>
              <p className="text-muted-foreground">
                We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
            </section>

            <section id="contact" className="scroll-mt-24 pt-12">
              <div className="bg-slate-900 text-white p-8 rounded-3xl text-center">
                 <Mail className="mx-auto mb-4 text-primary" size={32} />
                 <h3 className="text-2xl font-bold mb-2">Legal Contact</h3>
                 <p className="text-slate-400 mb-6">If you have any questions regarding these Terms, please contact our legal department.</p>
                 <a 
                    href={`mailto:${contactEmail}`} 
                    className="inline-block bg-primary text-white font-bold py-3 px-8 rounded-full hover:bg-primary/90 transition-colors"
                  >
                   {contactEmail}
                 </a>
              </div>
            </section>

          </article>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
